import React from "react";
import {
    HeartPulse,
    Target,
    Waves,
    TrendingUp,
    BatteryCharging,
    Moon,
} from "lucide-react";
import {
    LOGO_URL,
    PHONE_DISPLAY,
    EMAIL,
    INSTAGRAM,
    waUrl,
} from "../lib/constants";

/**
 * 2-page A4 corporate brochure for The Reset Sanctuary.
 * Page 1: Problem · Solution · Science · Benefits
 * Page 2: Equipment · Tatva K · Packages · Payment · Contact
 * View at /brochure-corporate. Print → Save as PDF.
 * Compliant wording: "Precision Frequency Recovery", "Frequency Practitioner".
 */

const GOLD = "#c9a227";
const GOLD_LINE = "rgba(201,162,39,0.25)";
const GOLD_LINE_SOFT = "rgba(201,162,39,0.15)";

const PageFrame = ({ children, testid }) => (
    <article
        className="brochure-page brochure-corp-page"
        data-testid={testid}
        style={{
            width: 794,
            minHeight: 1123,
            maxWidth: "100%",
            margin: "0 auto 32px",
            backgroundColor: "#0a0a0a",
            color: "#bbb",
            boxShadow:
                "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,162,39,0.18)",
            padding: "40px 44px 32px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: "Inter, sans-serif",
            display: "flex",
            flexDirection: "column",
        }}
    >
        <div
            style={{
                position: "absolute",
                inset: 18,
                border: "1px solid rgba(201,162,39,0.22)",
                pointerEvents: "none",
            }}
        />
        {children}
    </article>
);

const PageHeader = ({ kicker }) => (
    <header
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            marginBottom: 16,
        }}
    >
        <img
            src={LOGO_URL}
            alt="The Reset Sanctuary"
            style={{ height: 60 }}
        />
        <div style={{ textAlign: "right" }}>
            <p
                style={{
                    color: GOLD,
                    fontSize: 9,
                    letterSpacing: "0.35em",
                    textTransform: "uppercase",
                    margin: 0,
                    fontWeight: 500,
                }}
            >
                {kicker}
            </p>
            <p
                style={{
                    color: "#888",
                    fontSize: 8.5,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    margin: "4px 0 0",
                }}
            >
                Dubai · RAK
            </p>
        </div>
    </header>
);

const PageFooter = ({ pageNum }) => (
    <div
        style={{
            marginTop: "auto",
            paddingTop: 14,
            borderTop: `1px solid ${GOLD_LINE_SOFT}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            fontSize: 8,
            color: "#666",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
        }}
    >
        <span>theresetsanctuary.org</span>
        <span style={{ color: GOLD }}>The Reset Sanctuary</span>
        <span>Page {pageNum} / 2</span>
    </div>
);

const SectionTitle = ({ kicker, title, color = "#fff" }) => (
    <div style={{ marginBottom: 12 }}>
        <p
            style={{
                color: GOLD,
                fontSize: 9,
                letterSpacing: "0.35em",
                textTransform: "uppercase",
                margin: "0 0 6px",
                fontWeight: 500,
            }}
        >
            {kicker}
        </p>
        <h2
            className="font-serif"
            style={{
                color,
                fontSize: 20,
                fontWeight: 500,
                margin: 0,
                lineHeight: 1.1,
                letterSpacing: "-0.005em",
            }}
        >
            {title}
        </h2>
    </div>
);

const Pillar = ({ title, body }) => (
    <div
        style={{
            padding: "12px 12px",
            borderTop: `1px solid ${GOLD_LINE}`,
        }}
    >
        <p
            style={{
                color: GOLD,
                fontSize: 8.5,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                margin: "0 0 6px",
                fontWeight: 500,
                lineHeight: 1.3,
            }}
        >
            {title}
        </p>
        <p
            style={{
                color: "#bbb",
                fontSize: 9,
                lineHeight: 1.55,
                margin: 0,
                fontWeight: 300,
            }}
        >
            {body}
        </p>
    </div>
);

const BenefitTile = ({ Icon, label, body, testid }) => (
    <div
        data-testid={testid}
        style={{
            padding: "16px 14px",
            border: `1px solid ${GOLD_LINE}`,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            backgroundColor: "rgba(201,162,39,0.025)",
        }}
    >
        <Icon size={26} color={GOLD} strokeWidth={1.4} />
        <p
            style={{
                color: "#fff",
                fontSize: 10,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                margin: 0,
                fontWeight: 500,
                lineHeight: 1.25,
            }}
        >
            {label}
        </p>
        <p
            style={{
                color: "#aaa",
                fontSize: 8.5,
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 300,
            }}
        >
            {body}
        </p>
    </div>
);

const Package = ({ tier, price, headline, items, featured }) => (
    <div
        style={{
            padding: "18px 16px 20px",
            border: featured ? `1px solid ${GOLD}` : `1px solid ${GOLD_LINE}`,
            backgroundColor: featured ? "rgba(201,162,39,0.04)" : "transparent",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            height: "100%",
        }}
        data-testid={`corp-package-${tier.toLowerCase()}`}
    >
        {featured && (
            <span
                style={{
                    position: "absolute",
                    top: -10,
                    left: 14,
                    backgroundColor: GOLD,
                    color: "#0a0a0a",
                    fontSize: 8,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    padding: "3px 10px",
                    fontWeight: 600,
                }}
            >
                Most Chosen
            </span>
        )}
        <p
            style={{
                color: GOLD,
                fontSize: 9,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                margin: 0,
                fontWeight: 500,
            }}
        >
            {tier}
        </p>
        <p
            className="font-serif"
            style={{
                color: "#fff",
                fontSize: 22,
                fontWeight: 400,
                margin: 0,
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
            }}
        >
            AED {price}
        </p>
        <p
            style={{
                color: "#bbb",
                fontSize: 9.5,
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 300,
            }}
        >
            {headline}
        </p>
        <div
            style={{
                borderTop: `1px solid ${GOLD_LINE_SOFT}`,
                marginTop: 2,
                paddingTop: 8,
            }}
        >
            {items.map((it, i) => (
                <p
                    key={i}
                    style={{
                        color: "#fff",
                        fontSize: 9.5,
                        margin: "3px 0",
                        lineHeight: 1.4,
                    }}
                >
                    · {it}
                </p>
            ))}
        </div>
    </div>
);

const BrochureCorporate = () => {
    const corpWa = waUrl(
        "Hi, I'd like to book a corporate team reset session at The Reset Sanctuary.",
    );

    return (
        <main
            data-testid="brochure-corporate-page"
            style={{
                backgroundColor: "#0a0a0a",
                minHeight: "100vh",
                padding: "40px 16px 80px",
                color: "#bbb",
            }}
        >
            {/* Screen-only print button */}
            <div
                className="brochure-screen-only"
                style={{
                    maxWidth: 794,
                    margin: "0 auto 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                }}
            >
                <p
                    style={{
                        color: "#888",
                        fontSize: 12,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    Corporate Brochure — A4 · 2 pages
                </p>
                <button
                    onClick={() => window.print()}
                    className="btn-gold-solid"
                    data-testid="brochure-corp-print-btn"
                    style={{
                        cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                    }}
                >
                    Save as PDF / Print
                </button>
            </div>

            {/* ============== PAGE 1 ============== */}
            <PageFrame testid="brochure-corp-a4">
                <PageHeader kicker="For High-Performing Teams" />

                {/* HERO */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 16,
                    }}
                >
                    <p
                        style={{
                            color: GOLD,
                            fontSize: 9,
                            letterSpacing: "0.35em",
                            textTransform: "uppercase",
                            margin: "0 0 8px",
                            fontWeight: 500,
                        }}
                    >
                        For high-performing teams that never switch off
                    </p>
                    <h1
                        className="font-serif"
                        style={{
                            color: "#fff",
                            fontSize: 36,
                            fontWeight: 400,
                            lineHeight: 1.02,
                            letterSpacing: "-0.025em",
                            margin: "0 0 10px",
                            textTransform: "uppercase",
                        }}
                    >
                        Deeper Recovery.{" "}
                        <span style={{ color: GOLD }}>Higher Performance.</span>
                    </h1>
                    <p
                        style={{
                            color: "#bbb",
                            fontSize: 11,
                            lineHeight: 1.6,
                            margin: 0,
                            maxWidth: 640,
                            fontWeight: 300,
                        }}
                    >
                        Evidence-informed sound and frequency work designed to
                        reset the nervous system, accelerate recovery, and
                        elevate performance across executives, teams, and
                        founders.
                    </p>
                </section>

                {/* HERO IMAGE */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 18,
                        width: "100%",
                        aspectRatio: "1376 / 520",
                        overflow: "hidden",
                        border: `1px solid ${GOLD_LINE}`,
                    }}
                    data-testid="brochure-corp-hero-image"
                >
                    <img
                        src="/brochure-assets/corp-hero.jpg"
                        alt="The Reset Sanctuary — Dubai boardroom with recovery equipment"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center 40%",
                            display: "block",
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(180deg, rgba(10,10,10,0) 55%, rgba(10,10,10,0.6) 100%)",
                            pointerEvents: "none",
                        }}
                    />
                </section>

                {/* PROBLEM + SOLUTION */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 16,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                    }}
                >
                    <div data-testid="corp-problem">
                        <SectionTitle kicker="The Problem" title="Always on. Never recovering." />
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.6,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            Executives and founders run hot — chronic
                            sympathetic load, shallow sleep, brain fog, and
                            burnout disguised as productivity. Conventional
                            wellness rarely reaches the nervous system at the
                            depth required to truly reset.
                        </p>
                    </div>
                    <div data-testid="corp-solution">
                        <SectionTitle kicker="The Solution" title="Precision Frequency Recovery." color={GOLD} />
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.6,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            A protocol built on PEMF, vibroacoustic resonance,
                            binaural entrainment, and tuned frequencies —
                            delivered on-site, in-villa, or in private
                            session. A measurable downshift, in 60 minutes.
                        </p>
                    </div>
                </section>

                {/* SCIENCE — modalities */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 16,
                    }}
                    data-testid="corp-science"
                >
                    <SectionTitle kicker="The Science" title="Six modalities. One protocol." />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "2px 14px",
                        }}
                    >
                        <Pillar
                            title="PEMF Mat"
                            body="Pulsed electromagnetic field stimulation. Enhances cellular recovery, circulation, reduces inflammation."
                        />
                        <Pillar
                            title="Frequency Generator"
                            body="Precision frequencies that restore balance and optimise vitality at the autonomic level."
                        />
                        <Pillar
                            title="Vibroacoustic Bed"
                            body="Low-frequency mechanical vibration to biological tissue. Supports nervous system regulation."
                        />
                        <Pillar
                            title="Binaural Beats"
                            body="Brainwave entrainment for deep relaxation, cognitive clarity, and mental resilience."
                        />
                        <Pillar
                            title="Singing Bowls"
                            body="Harmonic resonance for autonomic downshift and energetic alignment."
                        />
                        <Pillar
                            title="Tuning Forks · 432/528/174/741 Hz"
                            body="Targeted frequency points calibrated to the body's recovery and regulation bands."
                        />
                    </div>
                </section>

                {/* BENEFITS — 6 icon tiles */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                    data-testid="corp-benefits"
                >
                    <SectionTitle kicker="The Benefits" title="What teams experience." />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 10,
                        }}
                    >
                        <BenefitTile
                            Icon={HeartPulse}
                            label="Enhanced Recovery"
                            body="Faster autonomic reset between cycles of high demand."
                            testid="benefit-recovery"
                        />
                        <BenefitTile
                            Icon={Target}
                            label="Improved Focus"
                            body="Sharper executive function and decision clarity."
                            testid="benefit-focus"
                        />
                        <BenefitTile
                            Icon={Waves}
                            label="Stress Reduction"
                            body="Measurable downshift in sympathetic load."
                            testid="benefit-stress"
                        />
                        <BenefitTile
                            Icon={TrendingUp}
                            label="Peak Performance"
                            body="Compounding gains in output and resilience."
                            testid="benefit-peak"
                        />
                        <BenefitTile
                            Icon={BatteryCharging}
                            label="Sustained Energy"
                            body="Stable energy across long, high-pressure days."
                            testid="benefit-energy"
                        />
                        <BenefitTile
                            Icon={Moon}
                            label="Sleep Optimisation"
                            body="Deeper, more restorative sleep architecture."
                            testid="benefit-sleep"
                        />
                    </div>
                </section>

                <PageFooter pageNum={1} />
            </PageFrame>

            {/* ============== PAGE 2 ============== */}
            <PageFrame testid="brochure-corp-a4-page2">
                <PageHeader kicker="Equipment · Practitioner · Packages" />

                {/* EQUIPMENT SHOWCASE */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 16,
                    }}
                    data-testid="brochure-corp-equipment"
                >
                    <SectionTitle kicker="Signature Equipment" title="Engineered for measurable reset." />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 14,
                        }}
                    >
                        {[
                            {
                                src: "/brochure-assets/pemf-mat.jpg",
                                label: "PEMF Mat",
                                caption:
                                    "Pulsed electromagnetic stimulation for cellular recovery and inflammation reduction.",
                                testid: "equipment-pemf",
                            },
                            {
                                src: "/brochure-assets/tuning-forks.jpg",
                                label: "Tuning Forks",
                                caption:
                                    "Calibrated to 432 · 528 · 174 · 741 Hz — the body's recovery and regulation bands.",
                                testid: "equipment-forks",
                            },
                        ].map((item) => (
                            <div
                                key={item.testid}
                                data-testid={item.testid}
                                style={{
                                    border: `1px solid ${GOLD_LINE}`,
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        aspectRatio: "16 / 10",
                                        overflow: "hidden",
                                    }}
                                >
                                    <img
                                        src={item.src}
                                        alt={item.label}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                </div>
                                <div style={{ padding: "10px 14px 12px" }}>
                                    <p
                                        style={{
                                            color: GOLD,
                                            fontSize: 9,
                                            letterSpacing: "0.32em",
                                            textTransform: "uppercase",
                                            margin: "0 0 4px",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {item.label}
                                    </p>
                                    <p
                                        style={{
                                            color: "#bbb",
                                            fontSize: 9.5,
                                            lineHeight: 1.45,
                                            margin: 0,
                                            fontWeight: 300,
                                        }}
                                    >
                                        {item.caption}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* PRACTITIONER */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 16,
                        padding: "14px 16px",
                        border: `1px solid ${GOLD_LINE}`,
                        display: "grid",
                        gridTemplateColumns: "70px 1fr",
                        gap: 14,
                        alignItems: "center",
                    }}
                >
                    <img
                        src="/brochure-assets/tatva.png"
                        alt="Tatva K"
                        style={{
                            width: 70,
                            height: 70,
                            objectFit: "cover",
                            objectPosition: "center 25%",
                            borderRadius: "50%",
                            border: `1px solid rgba(201,162,39,0.4)`,
                            display: "block",
                        }}
                        data-testid="brochure-corp-tatva-photo"
                    />
                    <div>
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 9,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                margin: "0 0 4px",
                                fontWeight: 500,
                            }}
                        >
                            Led by Tatva K
                        </p>
                        <p
                            style={{
                                color: "#fff",
                                fontSize: 9.5,
                                lineHeight: 1.55,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            150M+ aggregate streams · 182K monthly listeners · TEDx
                            speaker · Reiki Grandmaster · ISTA & IPHM
                            Certified · Berklee College of Music — Music
                            Therapy Masterclass · Featured by BBC Radio 1
                            (Bobby Friction), BBC Asian Network & Rolling
                            Stones India · Curated alongside Talvin Singh and
                            DJ Spooky.
                        </p>
                    </div>
                </section>

                {/* PACKAGES */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 16,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            marginBottom: 14,
                        }}
                    >
                        <h2
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 18,
                                fontWeight: 500,
                                margin: 0,
                                letterSpacing: "0.01em",
                            }}
                        >
                            Corporate Packages
                        </h2>
                        <p
                            style={{
                                color: "#888",
                                fontSize: 9,
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                margin: 0,
                            }}
                        >
                            On-site · In-Villa · Dubai & RAK
                        </p>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 14,
                        }}
                    >
                        <Package
                            tier="Starter"
                            price="7,999"
                            headline="Built for lean teams running on empty."
                            items={[
                                "Up to 10 people",
                                "2 team session days",
                                "1:1 reset (60 min)",
                                "Practitioner Q&A",
                            ]}
                        />
                        <Package
                            tier="Growth"
                            price="11,999"
                            headline="The sweet spot for scaling teams."
                            featured
                            items={[
                                "Up to 16 people",
                                "3 team session days",
                                "1:1 reset (60 min)",
                                "Founder briefing",
                                "Pre/post session debrief",
                            ]}
                        />
                        <Package
                            tier="Enterprise"
                            price="14,999"
                            headline="C-suite, leadership and full crew."
                            items={[
                                "Up to 20 people",
                                "3 team session days",
                                "1:1 reset (60 min)",
                                "2 private executive sessions (90 min)",
                                "Custom protocol",
                            ]}
                        />
                    </div>
                    <p
                        style={{
                            color: "#777",
                            fontSize: 8.5,
                            margin: "10px 0 0",
                            letterSpacing: "0.04em",
                        }}
                    >
                        All prices in AED. Bespoke and ongoing retainer
                        engagements available on request.
                    </p>
                </section>

                {/* PAYMENT + CONTACT */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: "1.1fr 1fr",
                        gap: 18,
                        marginBottom: 12,
                    }}
                >
                    <div>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 20,
                                fontWeight: 400,
                                margin: "0 0 6px",
                                letterSpacing: "-0.01em",
                            }}
                        >
                            Book Your{" "}
                            <span style={{ color: GOLD }}>Team Reset.</span>
                        </h2>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.55,
                                margin: 0,
                                maxWidth: 360,
                                fontWeight: 300,
                            }}
                        >
                            Reserve via WhatsApp. We confirm logistics, location
                            (your office, our space, or a private villa) and
                            schedule directly with your team.
                        </p>
                        <div
                            style={{
                                marginTop: 14,
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                            data-testid="brochure-corp-payments"
                        >
                            <p
                                style={{
                                    color: GOLD,
                                    fontSize: 8,
                                    letterSpacing: "0.3em",
                                    textTransform: "uppercase",
                                    margin: 0,
                                    fontWeight: 500,
                                }}
                            >
                                Flexible Payment Options
                            </p>
                            <div
                                style={{
                                    display: "flex",
                                    gap: 10,
                                    flexWrap: "wrap",
                                }}
                            >
                                <span
                                    style={{
                                        padding: "8px 14px",
                                        border: `1px solid ${GOLD_LINE}`,
                                        color: "#fff",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 10,
                                        backgroundColor:
                                            "rgba(255,255,255,0.02)",
                                    }}
                                    data-testid="brochure-corp-tabby"
                                >
                                    <img
                                        src="/brochure-assets/tabby-logo.png"
                                        alt="tabby"
                                        style={{
                                            height: 16,
                                            width: "auto",
                                            display: "block",
                                        }}
                                    />
                                    <span
                                        style={{
                                            color: "#bbb",
                                            fontSize: 8.5,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            borderLeft: `1px solid rgba(201,162,39,0.3)`,
                                            paddingLeft: 10,
                                        }}
                                    >
                                        4 interest-free payments
                                    </span>
                                </span>
                                <span
                                    style={{
                                        padding: "8px 14px",
                                        border: `1px solid ${GOLD_LINE}`,
                                        color: "#fff",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 10,
                                        backgroundColor:
                                            "rgba(255,255,255,0.02)",
                                    }}
                                    data-testid="brochure-corp-tamara"
                                >
                                    <img
                                        src="/brochure-assets/tamara-logo.png"
                                        alt="tamara"
                                        style={{
                                            height: 16,
                                            width: "auto",
                                            display: "block",
                                        }}
                                    />
                                    <span
                                        style={{
                                            color: "#bbb",
                                            fontSize: 8.5,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            borderLeft: `1px solid rgba(201,162,39,0.3)`,
                                            paddingLeft: 10,
                                        }}
                                    >
                                        Split your payment
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            textAlign: "right",
                            fontFamily: "Inter, sans-serif",
                        }}
                    >
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 9,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                margin: "0 0 6px",
                                fontWeight: 500,
                            }}
                        >
                            Reserve via WhatsApp
                        </p>
                        <p
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 18,
                                fontWeight: 500,
                                margin: "0 0 6px",
                                letterSpacing: "0.02em",
                            }}
                        >
                            {PHONE_DISPLAY}
                        </p>
                        <p style={{ color: "#bbb", fontSize: 10, margin: "0 0 2px" }}>
                            {EMAIL}
                        </p>
                        <p style={{ color: "#bbb", fontSize: 10, margin: "0 0 2px" }}>
                            theresetsanctuary.org
                        </p>
                        <p style={{ color: "#bbb", fontSize: 10, margin: "0 0 10px" }}>
                            Instagram {INSTAGRAM}
                        </p>
                        <a
                            href={corpWa}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="brochure-corp-cta"
                            className="brochure-screen-only"
                            style={{
                                display: "inline-block",
                                color: "#0a0a0a",
                                backgroundColor: GOLD,
                                padding: "10px 18px",
                                fontSize: 11,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                fontWeight: 600,
                                textDecoration: "none",
                            }}
                        >
                            Reserve Corporate Session
                        </a>
                    </div>
                </section>

                {/* DISCLAIMER */}
                <p
                    style={{
                        color: "#666",
                        fontSize: 7,
                        lineHeight: 1.5,
                        textAlign: "center",
                        margin: "0 0 10px",
                        position: "relative",
                        zIndex: 1,
                        paddingTop: 8,
                        borderTop: `1px solid ${GOLD_LINE_SOFT}`,
                    }}
                >
                    The Reset Sanctuary offers wellness and recovery sessions
                    through precision sound and frequency work. We do not
                    diagnose, treat, or cure medical conditions. Our services
                    are complementary wellness practices, not medical care.
                    Please consult a licensed healthcare provider for medical
                    concerns. © 2026 Tatva K.
                </p>

                <PageFooter pageNum={2} />
            </PageFrame>
        </main>
    );
};

export default BrochureCorporate;
